import { createReducer, on } from '@ngrx/store';
import {
  changeAdminCartItemQuantity,
  clearAdminCart,
  decrementAdminCartItem,
  deleteAdminCartItem,
  incrementAdminCartItem,
  loadAdminCartData,
} from './admin-cart.state';

const initialState: any = { items: [] };

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const sameId = (a: any, b: any) => String(a) === String(b);

// Updates the item's quantity AND its own line totals
const scaleItem = (i: any, newQty: number) => {
  const oldQty = +i.quantity || 1;
  const perUnit = (total: any) => (total != null ? round2((+total / oldQty) * newQty) : total);

  return {
    ...i,
    quantity: newQty.toString(),
    totalBusinessPrice: perUnit(i.totalBusinessPrice),
    totalBusinessPriceAfterDiscount: perUnit(i.totalBusinessPriceAfterDiscount),
    totalCustomerPrice: perUnit(i.totalCustomerPrice),
  };
};

// Line totals, with fallbacks when the backend didn't send a line total
const lineTotals = (items: any[]) =>
  items.reduce(
    (acc, i) => {
      const product = i?.customerProduct ?? i?.item?.customerProduct ?? i?.item ?? i;
      const qty = +(i?.quantity ?? 0);

      const business = i.totalBusinessPrice != null ? +i.totalBusinessPrice : +(product?.businessPrice ?? 0) * qty;
      const final = i.totalBusinessPriceAfterDiscount != null ? +i.totalBusinessPriceAfterDiscount : business;
      const customer = i.totalCustomerPrice != null ? +i.totalCustomerPrice : +(product?.customerPrice ?? 0) * qty;

      acc.business += business;
      acc.final += final;
      acc.customer += customer;
      acc.qty += qty;
      return acc;
    },
    { business: 0, final: 0, customer: 0, qty: 0 },
  );

// Cart-level discount (pharmacy segment) the backend applied on top of the line totals,
// captured on load so recalculations after increment/decrement/delete keep it
const segmentRatio = (cart: any) => {
  const lines = lineTotals(cart?.items ?? []).final;
  const final = +cart?.finalBusinessPrice;
  return lines > 0 && Number.isFinite(final) ? final / lines : 1;
};

const recalcAdminCart = (state: any, items: any[]) => {
  const totals = lineTotals(items);
  return {
    ...state,
    items,
    totalBusinessPrice: round2(totals.business),
    finalBusinessPrice: round2(Math.max(0, totals.final * (state.segmentRatio ?? 1))),
    finalCustomerPrice: round2(totals.customer),
    totalQuantity: totals.qty.toString(),
  };
};

const adminCartReducer = createReducer(
  initialState,
  on(loadAdminCartData, (_, { data }) => (data ? { ...data, segmentRatio: segmentRatio(data) } : data)),

  on(incrementAdminCartItem, (state: any, { cartItemId }) => {
    if (!state?.items) return state;
    const items = state.items.map((i: any) => (sameId(i.id, cartItemId) ? scaleItem(i, +i.quantity + 1) : i));
    return recalcAdminCart(state, items);
  }),

  on(decrementAdminCartItem, (state: any, { cartItemId }) => {
    if (!state?.items) return state;
    const items = state.items.map((i: any) =>
      sameId(i.id, cartItemId) ? scaleItem(i, Math.max(1, +i.quantity - 1)) : i,
    );
    return recalcAdminCart(state, items);
  }),

  on(changeAdminCartItemQuantity, (state: any, { cartItemId, quantity }) => {
    if (!state?.items) return state;
    if (!state.items.some((i: any) => sameId(i.id, cartItemId))) return state;
    const items = state.items.map((i: any) => (sameId(i.id, cartItemId) ? scaleItem(i, Math.max(1, +quantity)) : i));
    return recalcAdminCart(state, items);
  }),

  on(deleteAdminCartItem, (state: any, { cartItemId }) => {
    if (!state?.items) return state;
    const items = state.items.filter((i: any) => !sameId(i.id, cartItemId));
    return recalcAdminCart(state, items);
  }),

  on(clearAdminCart, (state: any) => recalcAdminCart(state, [])),
);

export { adminCartReducer };
